import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@/config/Grid/Grid";
import Button from "@/components/Button/Button";
import IconButton from "@/components/IconButton/IconButton";
import SettingsRow from "@/components/SettingsRow/SettingsRow";
import TransparentTile from "@/components/TransparentTile/TransparentTile";
import SignInDrawer from "@/components/SignInDrawer/SignInDrawer";
import { useGoBack } from "@/hooks/useGoBack";
import {
  useIdentityPromptSeen,
  useIdentityVerified,
  useLoyaltyActions,
  useLoyaltyContact,
  useLoyaltyPoints,
} from "@/store/useLoyaltyStore";
import { useVenuesDataStore } from "@/store/useVenuesDataStore";
import {
  PRIVACY_POLICY_URL,
  TERMS_URL,
  supportMailtoUrl,
} from "@/config/support";
import { allergensPath, loyaltyPath, tablePath } from "@/routes/paths";
import styles from "./Account.module.scss";

const EXTERNAL_ICON = "las la-external-link-alt";

/**
 * Everything about the guest rather than about the food: who they are, what
 * their points are worth, and the legal and support links.
 */
function AccountPage() {
  const { restaurantId = "", tableId = "" } = useParams<{
    restaurantId: string;
    tableId: string;
  }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useGoBack(tablePath(restaurantId, tableId));

  const points = useLoyaltyPoints();
  const contact = useLoyaltyContact();
  const identityVerified = useIdentityVerified();
  const identityPromptSeen = useIdentityPromptSeen();
  const { markIdentityPromptSeen, signOut } = useLoyaltyActions();

  const venue = useVenuesDataStore((state) => state.venue);
  const tableNumber = useVenuesDataStore((state) => state.tableNumber);

  /**
   * Opens by itself on a first visit, because points that follow a guest
   * between devices are the whole reason to give a contact at all.
   */
  const [signInOpen, setSignInOpen] = useState(
    () => !identityVerified && !identityPromptSeen,
  );

  useEffect(() => {
    if (signInOpen) markIdentityPromptSeen();
  }, [signInOpen, markIdentityPromptSeen]);

  const openSupportMail = () => {
    const url = supportMailtoUrl(
      t("account.support.subject"),
      t("account.support.body", {
        venue: venue?.name ?? "",
        table: tableNumber ?? "",
      }),
    );
    // Handing the guest to their mail client, so a plain assignment rather
    // than a new tab that would be left behind empty.
    window.location.href = url;
  };

  return (
    <>
      <Grid>
        <div className={`start-1 col-4 ${styles.accountWrapper}`}>
          <div className={styles.accountHeaderContainer}>
            <div className={styles.accountHeader}>
              <IconButton
                iconName="las la-angle-left icon-big"
                onClick={goBack}
                color="primary"
                variant="text"
              />
              <p className={`body-bold-large ${styles.accountTitle}`}>
                {t("account.title")}
              </p>
              <IconButton
                iconName=""
                onClick={() => {}}
                color="primary"
                variant="text"
              />
            </div>
          </div>

          <div className={styles.accountContent}>
            {/* Signed in there is nothing to do here, so it is a line of text
                rather than a row that offers signing in again. Signed out it
                is worth a pitch, in the shape of the points tile. */}
            {identityVerified ? (
              <div className={styles.signedIn}>
                <p className={`body-medium ${styles.signedInLabel}`}>
                  {t("account.signedIn")}
                </p>
                <p className={`body-bold-medium ${styles.signedInContact}`}>
                  {contact}
                </p>
              </div>
            ) : (
              <TransparentTile>
                <div className={styles.signInTileContent}>
                  <p className={`body-medium ${styles.signInTileTitle}`}>
                    {t("loyalty.signIn.title")}
                  </p>
                  <p className={`body-small ${styles.accountHint}`}>
                    {t("loyalty.signIn.intro")}
                  </p>
                  <Button
                    onClick={() => setSignInOpen(true)}
                    label={t("account.signIn")}
                    variant="outlined"
                    color="primary"
                    maxWidth
                    mobile={true}
                  />
                </div>
              </TransparentTile>
            )}

            <div className={styles.settingsGroup}>
              <SettingsRow
                label={t("account.points")}
                value={t("loyalty.cost", { count: points })}
                onClick={() => navigate(loyaltyPath(restaurantId, tableId))}
              />
              <SettingsRow
                label={t("account.allergens")}
                onClick={() => navigate(allergensPath(restaurantId, tableId))}
              />
            </div>

            <div className={styles.settingsGroup}>
              <SettingsRow
                label={t("account.privacyPolicy")}
                iconName={EXTERNAL_ICON}
                onClick={() => window.open(PRIVACY_POLICY_URL, "_blank")}
              />
              <SettingsRow
                label={t("account.terms")}
                iconName={EXTERNAL_ICON}
                onClick={() => window.open(TERMS_URL, "_blank")}
              />
              <SettingsRow
                label={t("account.support.label")}
                iconName={EXTERNAL_ICON}
                onClick={openSupportMail}
              />
            </div>

            {/* Only once there is an identity to leave. The balance is not
                signed out with it: it belongs to this phone. */}
            {identityVerified ? (
              <Button
                onClick={signOut}
                label={t("account.signOut")}
                variant="outlined"
                color="primary"
                maxWidth
                mobile={true}
              />
            ) : null}
          </div>
        </div>
      </Grid>

      <SignInDrawer open={signInOpen} onClose={() => setSignInOpen(false)} />
    </>
  );
}

export default AccountPage;
